import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { ScrollText, ChevronRight, Save, RotateCcw, Upload } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import { Badge, Card, EmptyState, ErrorState, LoadingState } from '@/components/ui'
import { cn } from '@/lib/utils.ts'

type AuditAction = 'create' | 'update' | 'delete' | 'login' | 'logout' | 'export' | 'send' | 'sync'

interface AuditEntry {
  id: string
  action: AuditAction
  resource: string
  resource_id: string | null
  details: Record<string, unknown> | null
  created_at: string
  user_id: string | null
  user_name: string | null
  user_email: string | null
}

interface ChangeOp {
  type: string
  id?: string
  path?: string
  name?: string
  old_path?: string
  new_path?: string
  old_name?: string
  new_name?: string
  old_rights?: string
  new_rights?: string
  groupId?: string
  letter?: string
  label?: string
  hostname?: string
  uncPath?: string
  serverId?: string
}

interface Props {
  customerId: string
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    })
  } catch {
    return iso
  }
}

function summarizeChange(c: ChangeOp): string {
  switch (c.type) {
    case 'create_folder': return `Ordner „${c.path}" angelegt`
    case 'rename_folder': return `Ordner „${c.old_path}" → „${c.new_path}"`
    case 'warn_delete_folder': return `Ordner „${c.path}" gelöscht`
    case 'create_group': return `Gruppe „${c.name}" angelegt`
    case 'rename_group': return `Gruppe „${c.old_name}" → „${c.new_name}"`
    case 'warn_delete_group': return `Gruppe „${c.name}" gelöscht`
    case 'create_ou': return `OU „${c.name}" angelegt`
    case 'rename_ou': return `OU „${c.old_name}" → „${c.new_name}"`
    case 'warn_delete_ou': return `OU „${c.name}" gelöscht`
    case 'create_server': return `Server „${c.name}" (${c.hostname}) angelegt`
    case 'rename_server': return `Server „${c.old_name}" → „${c.new_name}"`
    case 'warn_delete_server': return `Server „${c.name}" gelöscht`
    case 'create_share': return `Share „${c.uncPath}" angelegt`
    case 'rename_share': return `Share „${(c as ChangeOp & { old_unc?: string }).old_unc}" → „${(c as ChangeOp & { new_unc?: string }).new_unc}"`
    case 'create_drive': return `Laufwerk ${c.letter}: „${c.label}" angelegt`
    case 'update_drive': return `Laufwerk ${c.letter}: ${(c as ChangeOp & { old_target?: string }).old_target} → ${(c as ChangeOp & { new_target?: string }).new_target}`
    case 'warn_delete_drive': return `Laufwerk ${c.letter}: „${c.label}" entfernt`
    case 'update_permissions': return `Berechtigung „${c.path}" (${c.groupId}): ${c.old_rights} → ${c.new_rights}`
    default: return c.type
  }
}

interface EntryMeta {
  kind: 'save' | 'restore' | 'export' | 'other'
  title: string
  icon: typeof Save
  color: string
}

function classifyEntry(e: AuditEntry): EntryMeta {
  const d = e.details ?? {}
  if (e.action === 'export') {
    return { kind: 'export', title: 'Deployment exportiert', icon: Upload, color: 'text-blue-600' }
  }
  if (e.action === 'update' && d['action_type'] === 'restore') {
    const fromVer = d['restoredFromVersion']
    return {
      kind: 'restore',
      title: `Wiederhergestellt aus v${fromVer ?? '?'}`,
      icon: RotateCcw,
      color: 'text-amber-600',
    }
  }
  if (e.action === 'update' && typeof d['version'] === 'number') {
    const ver = d['version']
    return { kind: 'save', title: `Gespeichert (v${ver})`, icon: Save, color: 'text-green-600' }
  }
  return { kind: 'other', title: e.action, icon: ScrollText, color: 'text-gray-600' }
}

export function AuditTab({ customerId }: Props) {
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const { data, isLoading, error, refetch } = useQuery<{ entries: AuditEntry[] }>({
    queryKey: ['it-audit', customerId],
    queryFn: () =>
      apiClient.get(`/it-structures/customers/${customerId}/audit`).then((r) => r.data),
    enabled: !!customerId,
  })

  const entries = useMemo(() => data?.entries ?? [], [data])

  function toggle(id: string) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <ScrollText className="w-5 h-5 text-brand-500" />
        <h2 className="text-body font-semibold text-gray-900">Verlauf / Audit-Log</h2>
        <span className="text-caption text-gray-500">
          Chronologische Übersicht aller Änderungen an dieser Kundenstruktur
        </span>
      </div>

      {isLoading && <LoadingState label="Lade Audit-Log…" />}

      {error && (
        <ErrorState
          size="sm"
          title="Audit-Log konnte nicht geladen werden"
          description={error instanceof Error ? error.message : 'Unbekannter Fehler'}
          onRetry={() => refetch()}
        />
      )}

      {!isLoading && !error && entries.length === 0 && (
        <Card>
          <EmptyState
            size="sm"
            title="Noch keine Einträge"
            description="Für diesen Kunden wurden noch keine Änderungen erfasst."
          />
        </Card>
      )}

      {entries.length > 0 && (
        <Card>
          <div className="divide-y divide-gray-100">
            {entries.map((e) => {
              const meta = classifyEntry(e)
              const Icon = meta.icon
              const isOpen = expanded.has(e.id)
              const details = e.details ?? {}
              const changes = Array.isArray(details['changes'])
                ? (details['changes'] as ChangeOp[])
                : null
              const appliedOps = Array.isArray(details['appliedOpIds'])
                ? (details['appliedOpIds'] as string[])
                : null
              const hasDetails = (changes && changes.length > 0)
                || (appliedOps && appliedOps.length > 0)
                || (details['notes'] != null && details['notes'] !== '')

              return (
                <div key={e.id}>
                  <button
                    onClick={() => hasDetails && toggle(e.id)}
                    disabled={!hasDetails}
                    className={cn(
                      'w-full text-left px-4 py-3 flex items-start gap-3 transition-colors',
                      hasDetails ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default',
                    )}
                  >
                    <ChevronRight
                      className={cn(
                        'w-4 h-4 mt-0.5 flex-shrink-0 transition-transform text-gray-400',
                        isOpen && 'rotate-90',
                        !hasDetails && 'invisible',
                      )}
                    />
                    <Icon className={cn('w-4 h-4 mt-0.5 flex-shrink-0', meta.color)} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-body font-medium text-gray-900">{meta.title}</span>
                        {changes && (
                          <Badge variant="neutral" size="sm">
                            {changes.length} Änderung{changes.length === 1 ? '' : 'en'}
                          </Badge>
                        )}
                        {appliedOps && (
                          <Badge variant="warning" size="sm">
                            {appliedOps.length} Operation{appliedOps.length === 1 ? '' : 'en'} angewandt
                          </Badge>
                        )}
                      </div>
                      <div className="text-caption text-gray-500 mt-0.5">
                        {formatTime(e.created_at)}
                        {' · '}
                        {e.user_name ?? e.user_email ?? 'System'}
                      </div>
                      {details['notes'] != null && details['notes'] !== '' && (
                        <div className="text-caption text-gray-600 mt-1 italic line-clamp-2">
                          {String(details['notes'])}
                        </div>
                      )}
                    </div>
                  </button>

                  {isOpen && (
                    <div className="px-4 pb-3 pl-12 space-y-2">
                      {changes && changes.length > 0 && (
                        <div>
                          <div className="text-caption font-medium text-gray-700 mb-1">
                            Änderungen gegenüber Vorgängerversion:
                          </div>
                          <ul className="text-caption text-gray-600 space-y-0.5 list-disc list-inside">
                            {changes.slice(0, 50).map((c, i) => (
                              <li key={i}>{summarizeChange(c)}</li>
                            ))}
                            {changes.length > 50 && (
                              <li className="text-gray-400">… und {changes.length - 50} weitere</li>
                            )}
                          </ul>
                        </div>
                      )}
                      {appliedOps && appliedOps.length > 0 && (
                        <div>
                          <div className="text-caption font-medium text-gray-700 mb-1">
                            Wiederhergestellte Operationen:
                          </div>
                          <ul className="text-caption text-gray-600 font-mono space-y-0.5 list-disc list-inside">
                            {appliedOps.map((o) => (
                              <li key={o}>{o}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {details['deployment_id'] != null && (
                        <div className="text-caption text-gray-600">
                          Deployment-ID:{' '}
                          <span className="font-mono">{String(details['deployment_id'])}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </Card>
      )}
    </div>
  )
}
